/**
 * Standalone module for quick search. Does not depend on navigation elements.
 */
import Mustache from 'mustache';

export type QuickSearchSettings = {
  /** Search form element selector */
  formElementSelector: string;
  /** Input element selector */
  inputElementSelector: string;
  /** Results container element selector */
  resultsElementSelector: string;
  /** Results template element selector */
  resultsTemplateSelector: string;
  /** Minimum characters to start search */
  minCharacters: number;
  /** Search delay in ms */
  searchDelay: number;
  /** Api url */
  apiUrl: string;
  /** Language code */
  language: string;
  /** Site id */
  site: string;
};

class QuickSearch {
  #elements: {
    root: HTMLElement | null;
    form: HTMLFormElement | null;
    input: HTMLInputElement | null;
    results: HTMLElement | null;
  } = {
    root: null,
    form: null,
    input: null,
    results: null,
  };
  #templates: {
    results: string;
  } = {
    results: '',
  };
  settings: QuickSearchSettings;
  currentSearchTerm: string = '';
  results: {
    hits: any[];
    page: number;
    total_hits: number;
    term: string;
    showAllResults: string;
  } = {
    hits: [],
    page: 1,
    total_hits: 0,
    term: '',
    showAllResults: ''
  };
  controller: AbortController = new AbortController();
  timer: NodeJS.Timeout | null = null;
  processingClickEvent: string | null = null;

  constructor(element: HTMLElement, options: Partial<QuickSearchSettings>) {
    const defaultSettings = {
      formElementSelector: '[data-quick-search-form]',
      inputElementSelector: '[data-quick-search-form] [name="search"]',
      resultsElementSelector: '[data-quick-search-results]',
      resultsTemplateSelector: '[data-quick-search-results-template]',
      minCharacters: 3,
      searchDelay: 500,
      apiUrl: '/stage-ajax/siteSearch/',
      language: document.documentElement.lang || '',
      site: document.body.dataset.site || '',
    };

    const settings = Object.assign({}, defaultSettings, options);

    this.settings = settings;

    this.#elements = {
      root: element,
      form: element.querySelector(settings.formElementSelector),
      input: element.querySelector(settings.inputElementSelector),
      results: element.querySelector(settings.resultsElementSelector),
    };

    this.#templates = {
      results: element.querySelector(settings.resultsTemplateSelector)?.textContent || '',
    };

    this.#initListeners();

    console.log('quick-search.init()');
  }

  /**
   * Initialises the functionality.
   * @private
   */
  #initListeners(): void {
    this.#elements.input?.addEventListener('keyup', this.onInputKeyUp.bind(this));
    this.#elements.results?.addEventListener('click', this.onResultsClick.bind(this));
  }

  /**
   * Input keyup event handler
   */
  onInputKeyUp(event: KeyboardEvent): void {
    // Emit event so that other components can react to it
    if(event.key === 'Escape') {
      this.#elements.input?.dispatchEvent(new CustomEvent('quickSearch.escape', { bubbles: true, cancelable: false }));
    }

    // Do nothing if the value has not changed
    const value = this.#elements.input?.value.trim() || '';
    if(this.currentSearchTerm === value) {
      return;
    }

    // Search value is too short
    if(value.length < this.settings.minCharacters) {
      if(!value) {
        this.emptyResults();
      }
      return;
    }

    // Perform search
    this.currentSearchTerm = value;
    this.search(value);
  }

  /**
   * Result click event handler
   */
  onResultsClick(event: Event): void {
    const target = (event.target as HTMLElement).closest('[data-quick-search-item]') as HTMLElement;

    if(target) {
      event.preventDefault();

      this.#sendClickEvent({
        url: target.getAttribute('href') || '',
        id: target.dataset?.quickSearchItem || '',
        position: target.dataset?.quickSearchItemPosition || '',
        term: target.dataset?.quickSearchItemTerm || '',
      });
    }
  }

  /**
   * Fetches search results from server
   */
  search(value: string): void {
    this.controller.abort();
    this.controller = new AbortController();
    clearTimeout(this.timer as NodeJS.Timeout);

    this.timer = setTimeout(async () => {
      try {
        this.#elements.form?.classList.add('loading');
        fetch(this.settings.apiUrl + '?' + new URLSearchParams({
          'stage-language': this.settings.language,
          site: this.settings.site,
          context: 'quick_search',
          search: value
        }).toString(), {
          method: 'POST',
          signal: this.controller.signal,
        })
          .then(response => response.json())
          .then((response) => {
            response.showAllResults = this.#elements.form?.getAttribute('action') + '?search=' + response.term;
            this.results = response;
            this.#renderResults();
            this.showResults();
            this.#elements.form?.classList.remove('loading');
          })
          .catch((err) => {
            console.error(`[quick-search] ${err.message}`, err);
            this.#elements.form?.classList.remove('loading');
          });
        return true;
      } catch (err) {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.error(`[quick-search] ${err.message}`, err);
          this.#elements.form?.classList.remove('loading');
        }
      } finally {

      }
    }, this.settings.searchDelay);
  }

  /**
   * Send click event to server
   * @private
   */
  #sendClickEvent(data: { url: string, id: string, position: string, term: string }) {
    if(this.processingClickEvent) {
      return;
    }

    this.processingClickEvent = data.id;

    let locationChanging = false;
    const loadTargetPath = () => {
      if (data.url && !locationChanging) {
        locationChanging = true;
        window.location.href = data.url;
      }
    };

    try {
      fetch(`${this.settings.apiUrl}?stage-language=${this.settings.language}&context=reportClick`, {
        method: 'POST',
        signal: this.controller.signal,
        body: JSON.stringify({
          id: data.id,
          position: parseInt(data.position),
          term: data.term,
        }),
      }).then(() => {
        loadTargetPath();
      }).catch((err) => {
        loadTargetPath();
      });

      return true;
    } catch (err) {
      if(err instanceof Error && err.name !== 'AbortError') {
        console.error(`[quick-search] ${err.message}`, err);
      }
    }

    // Navigate to new url after small delay, even if ajax request has not completed
    setTimeout(loadTargetPath, 500);
  }

  /**
   * Render results view with current results.
   * @private
   */
  #renderResults(): void {
    const results = { ...this.results };
    if(this.#elements.results) {
      this.#elements.results.innerHTML = Mustache.render(this.#templates.results, results);
    }
  }

  /**
   * Shows results
   */
  showResults(): void {
    if(!this.results.term) {
      return;
    }
    this.#elements.results?.classList.add('open');
  }

  /**
   * Clears results
   */
  emptyResults(): void {
    if(this.#elements.results) {
      this.#elements.results.innerHTML = '';
      this.#elements.results.classList.remove('open');
    }
  }

}

export default QuickSearch;
